import { IconPlus, IconX } from "@tabler/icons-react";
import { Button, IconButton, Input, Text } from "@/components/ui";
import type { Notificacion, VariableDeEvento } from "../../../services/notificaciones-service";
import { CampoPlantilla } from "./CampoPlantilla";
import { ContenidoSms, ContenidoTelegram, ContenidoWebPush, ContenidoWhatsApp } from "./ContenidoCanalesExternos";

interface Props {
  notificacion: Notificacion;
  variables: VariableDeEvento[];
  onCambio: (cambio: Partial<Notificacion>) => void;
}

/** Campos de contenido propios de cada canal (cada tipo de notificación define los suyos). */
export function ContenidoCanal({ notificacion: n, variables, onCambio }: Props) {
  switch (n.canal) {
    case "Email":
      return (
        <>
          <CampoPlantilla etiqueta="Asunto" valor={n.asunto ?? ""} onCambio={(asunto) => onCambio({ asunto })} variables={variables} />
          <CampoPlantilla
            etiqueta="Cuerpo (HTML)"
            valor={n.cuerpoHtml ?? ""}
            onCambio={(cuerpoHtml) => onCambio({ cuerpoHtml })}
            variables={variables}
            multilinea
            filas={8}
            lenguaje="html"
            ayuda="Los valores de las variables se escapan como HTML (no se pueden inyectar etiquetas)."
          />
        </>
      );
    case "Campana":
      return (
        <>
          <CampoPlantilla etiqueta="Título" valor={n.titulo ?? ""} onCambio={(titulo) => onCambio({ titulo })} variables={variables} />
          <CampoPlantilla etiqueta="Texto" valor={n.texto ?? ""} onCambio={(texto) => onCambio({ texto })} variables={variables} multilinea filas={3} />
          <CampoPlantilla
            etiqueta="Enlace (opcional)"
            valor={n.enlace ?? ""}
            onCambio={(enlace) => onCambio({ enlace: enlace || null })}
            variables={variables}
            placeholder="/solicitudes/{{SolicitudId}}"
            ayuda="Ruta del portal que empiece con /."
          />
        </>
      );
    case "Webhook":
      return <ContenidoWebhook notificacion={n} variables={variables} onCambio={onCambio} />;
    case "Sms":
      return <ContenidoSms notificacion={n} variables={variables} onCambio={onCambio} />;
    case "WhatsApp":
      return <ContenidoWhatsApp notificacion={n} variables={variables} onCambio={onCambio} />;
    case "Telegram":
      return <ContenidoTelegram notificacion={n} variables={variables} onCambio={onCambio} />;
    case "WebPush":
      return <ContenidoWebPush notificacion={n} variables={variables} onCambio={onCambio} />;
  }
}

function ContenidoWebhook({ notificacion: n, variables, onCambio }: Props) {
  const encabezados = n.encabezados ?? [];
  const actualizar = (i: number, cambio: { nombre?: string; valor?: string }) =>
    onCambio({ encabezados: encabezados.map((e, j) => (j === i ? { ...e, ...cambio } : e)) });

  return (
    <>
      <Input label="URL (POST)" size="xs" placeholder="https://erp.proveedores.test/hooks/pagos" value={n.url ?? ""} onChange={(e) => onCambio({ url: e.target.value })} />
      <CampoPlantilla
        etiqueta="Cuerpo JSON"
        valor={n.cuerpoJson ?? ""}
        onCambio={(cuerpoJson) => onCambio({ cuerpoJson })}
        variables={variables}
        multilinea
        filas={9}
        lenguaje="json"
        ayuda='Los textos se escapan como JSON. Para números y booleanos usa la variable sin comillas: "total": {{Total}}'
      />
      <div className="space-y-1.5">
        <Text size="xs" fw={500}>Encabezados</Text>
        {encabezados.map((e, i) => (
          <div key={i} className="flex gap-1.5">
            <Input size="xs" wrapperClassName="flex-1" placeholder="Nombre" value={e.nombre} onChange={(ev) => actualizar(i, { nombre: ev.target.value })} />
            <Input size="xs" wrapperClassName="flex-1" placeholder="Valor" value={e.valor} onChange={(ev) => actualizar(i, { valor: ev.target.value })} />
            <IconButton size="xs" aria-label="Quitar encabezado" onClick={() => onCambio({ encabezados: encabezados.filter((_, j) => j !== i) })}>
              <IconX />
            </IconButton>
          </div>
        ))}
        <Button size="xs" variant="default" leftSection={<IconPlus size={12} />} onClick={() => onCambio({ encabezados: [...encabezados, { nombre: "", valor: "" }] })}>
          Agregar encabezado
        </Button>
        <Text size="xs" c="dimmed">Los valores quedan guardados en texto plano (p. ej. un token de autorización del destino).</Text>
      </div>
    </>
  );
}
